import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { Loader2 } from 'lucide-react';
import useAppStore from '../store';
import { supabase } from '../lib/supabase';
import SearchableSelect from './SearchableSelect';

interface ClientLocation {
  id: string;
  name: string;
  position_lat: number;
  position_lng: number;
  is_primary: boolean;
  client_id: string;
}

interface FuelDumpsterTaskFormProps {
  onSubmit: (task: {
    driverId: string;
    clientId: string;
    locationId: string;
    date: string;
    type: 'fuel' | 'dumpster';
    status?: 'pending' | 'waiting';
    fuelType?: 'fioul' | 'gnr' | 'gasoil';
    plannedLiters?: number;
    price?: number;
    dumpsterId?: string;
    dumpsterTaskType?: 'pose' | 'retrait' | 'echange';
    comment?: string;
  }) => void;
  onCancel: () => void;
}

const FuelDumpsterTaskForm: React.FC<FuelDumpsterTaskFormProps> = ({ onSubmit, onCancel }) => {
  const { drivers, clients, dumpsters } = useAppStore();
  const [taskType, setTaskType] = useState<'fuel' | 'dumpster'>('fuel');
  const [clientLocations, setClientLocations] = useState<ClientLocation[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const [formData, setFormData] = useState<{
    driverId: string;
    clientId: string;
    locationId: string;
    date: string;
    status: 'pending';
    fuelType: 'fioul' | 'gnr' | 'gasoil';
    plannedLiters: number;
    price: number;
    dumpsterId: string;
    dumpsterTaskType: 'pose' | 'retrait' | 'echange';
    comment: string;
  }>({
    driverId: '',
    clientId: '',
    locationId: '',
    date: format(new Date(), "yyyy-MM-dd'T'HH:mm"),
    status: 'pending',
    fuelType: 'fioul',
    plannedLiters: 1000,
    price: 1.2,
    dumpsterId: '',
    dumpsterTaskType: 'pose',
    comment: '',
  });

  useEffect(() => {
    if (formData.clientId) {
      const loadLocations = async () => {
        try {
          const { data, error } = await supabase
            .from('client_locations')
            .select('*')
            .eq('client_id', formData.clientId)
            .order('is_primary', { ascending: false });

          if (error) throw error;

          setClientLocations(data || []);
          
          const primaryLocation = data?.find(loc => loc.is_primary);
          if (primaryLocation) {
            setFormData(prev => ({ ...prev, locationId: primaryLocation.id }));
          } else {
            setFormData(prev => ({ ...prev, locationId: '' }));
          }
        } catch (error) {
          console.error('Error loading client locations:', error);
          alert('Erreur lors du chargement des points GPS');
        }
      };

      loadLocations();
    } else {
      setClientLocations([]);
      setFormData(prev => ({ ...prev, locationId: '' }));
    }
  }, [formData.clientId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.locationId) {
      alert('Veuillez sélectionner un point GPS');
      return;
    }

    setIsSubmitting(true);

    try {
      const task = {
        driverId: formData.driverId,
        clientId: formData.clientId,
        locationId: formData.locationId,
        date: formData.date,
        status: formData.status,
        type: taskType,
        comment: formData.comment.trim() || undefined,
        ...(taskType === 'fuel' ? {
          fuelType: formData.fuelType,
          plannedLiters: formData.plannedLiters,
          price: formData.price,
        } : {
          dumpsterId: formData.dumpsterId || undefined,
          dumpsterTaskType: formData.dumpsterTaskType,
        }),
      };

      // Log pour déboguer
      console.log('Submitting task with comment:', formData.comment);
      console.log('Final task object:', task);

      await onSubmit(task);
    } finally {
      setIsSubmitting(false);
    }
  };

  const driverOptions = drivers.map(driver => ({
    value: driver.id,
    label: `${driver.firstName} ${driver.lastName}`
  })).sort((a, b) => {
    const driverA = drivers.find(d => d.id === a.value);
    const driverB = drivers.find(d => d.id === b.value);
    if (!driverA || !driverB) return 0;

    // Tri par nom de famille, puis par prénom
    const lastNameComparison = driverA.lastName.localeCompare(driverB.lastName);
    if (lastNameComparison !== 0) return lastNameComparison;
    return driverA.firstName.localeCompare(driverB.firstName);
  });

  const clientOptions = clients.map(client => ({
    value: client.id,
    label: `${client.companyName} (${client.firstName} ${client.lastName})`
  }));

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-2xl">
        <div className="p-6 border-b">
          <h2 className="text-xl font-bold">
            Nouvelle tâche {taskType === 'fuel' ? 'carburant' : 'benne'}
          </h2>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="p-6 space-y-6">
            {/* Task Type Selection */}
            <div className="mb-6">
              <label className="block text-gray-700 font-medium mb-2">
                Type de tâche
              </label>
              <div className="grid grid-cols-2 gap-4">
                <button
                  type="button"
                  onClick={() => setTaskType('fuel')}
                  className={`p-4 rounded-lg border-2 text-center ${
                    taskType === 'fuel'
                      ? 'border-blue-800 bg-blue-50 text-blue-800'
                      : 'border-gray-200 hover:border-blue-800 hover:bg-blue-50'
                  }`}
                >
                  Carburant
                </button>
                <button
                  type="button"
                  onClick={() => setTaskType('dumpster')}
                  className={`p-4 rounded-lg border-2 text-center ${
                    taskType === 'dumpster'
                      ? 'border-blue-800 bg-blue-50 text-blue-800'
                      : 'border-gray-200 hover:border-blue-800 hover:bg-blue-50'
                  }`}
                >
                  Benne
                </button>
              </div>
            </div>

            {/* Common Fields */}
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-gray-700 font-medium mb-2">
                  Chauffeur
                </label>
                <SearchableSelect
                  options={driverOptions}
                  value={formData.driverId}
                  onChange={(value) => setFormData(prev => ({ ...prev, driverId: value }))}
                  placeholder="Sélectionner un chauffeur"
                  searchPlaceholder="Rechercher un chauffeur..."
                />
              </div>
              
              <div>
                <label className="block text-gray-700 font-medium mb-2">
                  Client
                </label>
                <SearchableSelect
                  options={clientOptions}
                  value={formData.clientId}
                  onChange={(value) => setFormData(prev => ({ ...prev, clientId: value }))}
                  placeholder="Sélectionner un client"
                  searchPlaceholder="Rechercher un client..."
                />
              </div>

              {formData.clientId && clientLocations.length > 0 && (
                <div className="col-span-2">
                  <label className="block text-gray-700 font-medium mb-2">
                    Point GPS
                  </label>
                  <select
                    value={formData.locationId}
                    onChange={(e) => setFormData(prev => ({ ...prev, locationId: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="">Sélectionner un point GPS</option>
                    {clientLocations.map(location => (
                      <option key={location.id} value={location.id}>
                        {location.name} {location.is_primary ? '(Principal)' : ''}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="col-span-2">
                <label className="block text-gray-700 font-medium mb-2">
                  Date et heure
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <input
                    type="date"
                    value={formData.date.split('T')[0]}
                    onChange={(e) => {
                      const time = formData.date.split('T')[1] || '07:00';
                      setFormData(prev => ({
                        ...prev,
                        date: `${e.target.value}T${time}`
                      }));
                    }}
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                  <select
                    value={formData.date.split('T')[1]?.slice(0, 5) || '07:00'}
                    onChange={(e) => {
                      const date = formData.date.split('T')[0];
                      setFormData(prev => ({
                        ...prev,
                        date: `${date}T${e.target.value}`
                      }));
                    }}
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    {Array.from({ length: 49 }, (_, i) => {
                      const hour = Math.floor(i / 4) + 7;
                      const minute = (i % 4) * 15;
                      const time = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
                      return hour < 20 ? <option key={time} value={time}>{time}</option> : null;
                    })}
                  </select>
                </div>
              </div>
            </div>

            {/* Fuel-specific Fields */}
            {taskType === 'fuel' && (
              <div className="grid grid-cols-3 gap-4 mb-4">
                <div>
                  <label className="block text-gray-700 font-medium mb-2">
                    Type de carburant
                  </label>
                  <select
                    value={formData.fuelType}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      fuelType: e.target.value as 'fioul' | 'gnr' | 'gasoil'
                    }))}
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="fioul">Fioul</option>
                    <option value="gnr">GNR</option>
                    <option value="gasoil">Gasoil blanc</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-gray-700 font-medium mb-2">
                    Litrage prévu
                  </label>
                  <input
                    type="number"
                    value={formData.plannedLiters}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      plannedLiters: parseInt(e.target.value) 
                    }))}
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                    min="1"
                  />
                </div>
                
                <div>
                  <label className="block text-gray-700 font-medium mb-2">
                    Prix (€/L)
                  </label>
                  <input
                    type="number"
                    value={formData.price}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      price: parseFloat(e.target.value) 
                    }))}
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                    min="0.01"
                    step="0.01"
                  />
                </div>
              </div>
            )}

            {/* Dumpster-specific Fields */}
            {taskType === 'dumpster' && (
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-gray-700 font-medium mb-2">
                    Benne
                  </label>
                  <SearchableSelect
                    options={[
                      { value: '', label: 'Sélectionner une benne' },
                      ...dumpsters
                        .filter(dumpster => dumpster.isActive !== false)
                        .map(dumpster => ({
                          value: dumpster.id,
                          label: `${dumpster.name} (${dumpster.number})`
                        }))
                    ]}
                    value={formData.dumpsterId}
                    onChange={(value) => setFormData(prev => ({ ...prev, dumpsterId: value }))}
                    placeholder="Sélectionner une benne"
                    searchPlaceholder="Rechercher par nom ou numéro..."
                  />
                </div>

                <div>
                  <label className="block text-gray-700 font-medium mb-2">
                    Type d'opération
                  </label>
                  <select
                    value={formData.dumpsterTaskType}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      dumpsterTaskType: e.target.value as 'pose' | 'retrait' | 'echange'
                    }))}
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="pose">Pose</option>
                    <option value="retrait">Retrait</option>
                    <option value="echange">Échange</option>
                  </select>
                </div>
              </div>
            )}

            {/* Commentaire (pour tous les types de tâches) */}
            <div className="col-span-2">
              <label className="block text-gray-700 font-medium mb-2">
                Commentaire (facultatif)
              </label>
              <textarea
                value={formData.comment}
                onChange={(e) => setFormData(prev => ({ ...prev, comment: e.target.value }))}
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows={3}
                placeholder="Instructions spécifiques pour le chauffeur..."
                maxLength={500}
              />
              <div className="text-xs text-gray-500 mt-1 text-right">
                {formData.comment.length}/500 caractères
              </div>
            </div>
          </div>

          <div className="p-6 border-t bg-gray-50">
            <div className="flex justify-end space-x-2">
              <button
                type="button"
                onClick={onCancel}
                disabled={isSubmitting}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Annuler
              </button>

              <button
                type="submit"
                disabled={isSubmitting}
                className="px-4 py-2 bg-blue-800 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
              >
                {isSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
                <span>{isSubmitting ? 'Création en cours...' : 'Créer'}</span>
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default FuelDumpsterTaskForm;